/**
 * Human-readable package size for priced offers.
 * Uses US retail conventions by category (not the global volume-unit toggle):
 * beer / seltzer / cider → fl oz cans; wine / liquor → mL or L bottles.
 */

import {
  formatMetric,
  formatMoney,
  ML_PER_US_FL_OZ,
  mlToFlOz,
  mlToLiters,
} from '@/lib/metrics';
import type { DrinkCategory, VolumeUnit } from '@/types/data';

const COMMON_FLOZ = [7, 8, 12, 16, 19.2, 22, 24, 32, 40];
const COMMON_ML = [50, 100, 200, 375, 700, 750, 1000, 1750];

function nearly(a: number, b: number, tol: number): boolean {
  return Math.abs(a - b) <= tol;
}

function isBeerLike(category?: DrinkCategory | null): boolean {
  return (
    category === 'beer' || category === 'seltzers' || category === 'cider'
  );
}

function isSpiritLike(category?: DrinkCategory | null): boolean {
  return category === 'liquor' || category === 'wine';
}

function formatAsFlOz(containerMl: number): string {
  const flOz = mlToFlOz(containerMl);
  for (const oz of COMMON_FLOZ) {
    const tol = oz >= 19 ? 0.4 : 0.25;
    if (nearly(flOz, oz, tol)) {
      return `${oz} fl oz`;
    }
  }
  return `${formatMetric(flOz, 1)} fl oz`;
}

function formatAsMetricBottle(containerMl: number): string {
  if (nearly(containerMl, 1000, 5)) return '1 L';
  if (nearly(containerMl, 1750, 15)) return '1.75 L';
  for (const ml of COMMON_ML) {
    const tol = ml >= 700 ? 8 : 3;
    if (nearly(containerMl, ml, tol)) {
      if (ml === 1000) return '1 L';
      if (ml === 1750) return '1.75 L';
      return `${ml} mL`;
    }
  }
  if (containerMl >= 1000) {
    return `${formatMetric(mlToLiters(containerMl), 2)} L`;
  }
  return `${formatMetric(containerMl, 0)} mL`;
}

/**
 * Package / bottle size copy for US shoppers.
 * Category conventions win over the catalog volume-unit preference so beer
 * never shows as "355 mL cans" when the user has mL selected for metrics.
 */
export function formatContainerVolume(
  containerMl: number,
  opts?: {
    category?: DrinkCategory | null;
    /** Only used for mixed-drinks / unknown categories. */
    volumeUnit?: VolumeUnit | 'auto';
  },
): string {
  if (!(containerMl > 0)) return '';

  if (isBeerLike(opts?.category)) {
    return formatAsFlOz(containerMl);
  }
  if (isSpiritLike(opts?.category)) {
    return formatAsMetricBottle(containerMl);
  }

  const prefer = opts?.volumeUnit ?? 'auto';
  if (prefer === 'fl_oz') return formatAsFlOz(containerMl);
  if (prefer === 'ml' || prefer === 'L') return formatAsMetricBottle(containerMl);

  // Unknown / mixed: fl oz for can-sized, metric for bottle-sized.
  const flOz = mlToFlOz(containerMl);
  if (flOz <= 40) return formatAsFlOz(containerMl);
  return formatAsMetricBottle(containerMl);
}

export function packageVesselWord(
  containerCount: number,
  category?: DrinkCategory | null,
): string {
  const plural = containerCount !== 1;
  if (isSpiritLike(category)) return plural ? 'bottles' : 'bottle';
  if (isBeerLike(category)) return plural ? 'cans' : 'can';
  return plural ? 'containers' : 'container';
}

/** e.g. "24 × 12 fl oz cans" or "750 mL bottle" */
export function formatPackageSize(
  containerCount: number,
  containerMl: number,
  opts?: {
    category?: DrinkCategory | null;
    volumeUnit?: VolumeUnit | 'auto';
  },
): string {
  if (!(containerCount > 0) || !(containerMl > 0)) return '';
  const volume = formatContainerVolume(containerMl, opts);
  if (!volume) return '';
  const vessel = packageVesselWord(containerCount, opts?.category);
  if (containerCount === 1) return `${volume} ${vessel}`;
  return `${containerCount} × ${volume} ${vessel}`;
}

/** e.g. "$26.99 · 24 × 12 fl oz cans" */
export function formatPackagePrice(
  packagePriceUsd: number | null | undefined,
  containerCount: number,
  containerMl: number,
  opts?: {
    category?: DrinkCategory | null;
    volumeUnit?: VolumeUnit | 'auto';
  },
): string {
  const size = formatPackageSize(containerCount, containerMl, opts);
  if (packagePriceUsd == null || !(packagePriceUsd > 0)) {
    return size ? `Price unavailable (${size})` : 'Price unavailable';
  }
  const dollars = formatMoney(packagePriceUsd);
  return size ? `${dollars} · ${size}` : dollars;
}

/** Approx. fl oz used when seeding US beer cans — exported for tests. */
export const US_12_FLOZ_ML = 12 * ML_PER_US_FL_OZ;
